/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
import React, { forwardRef, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Box, Text } from '@mantine/core';

interface RichTextEditorInputProps {
  value?: string;
  onChange?: (_value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  minHeight?: number;
  height?: number;
  uploadHandler?: (blobInfo: any, progress?: (percent: number) => void) => Promise<string>;
  withGallery?: boolean;
  withSlider?: boolean;
  controls?: Array<
    | 'bold'
    | 'italic'
    | 'underline'
    | 'strike'
    | 'clearFormatting'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'bulletList'
    | 'orderedList'
    | 'link'
    | 'unlink'
    | 'blockquote'
    | 'hr'
    | 'undo'
    | 'redo'
    | 'alignLeft'
    | 'alignCenter'
    | 'alignRight'
    | 'alignJustify'
    | 'superscript'
    | 'subscript'
    | 'highlight'
    | 'color'
  >;
}

const RichTextEditorInput = forwardRef<HTMLDivElement, RichTextEditorInputProps>(
  (
    {
      value = '',
      onChange,
      onBlur,
      placeholder = 'Start typing...',
      error,
      label,
      required = false,
      disabled = false,
      minHeight = 300,
      height,
      uploadHandler,
      withGallery = false,
      withSlider = false,
      controls = [
        'bold',
        'italic',
        'underline',
        'strike',
        'clearFormatting',
        'h1',
        'h2',
        'h3',
        'bulletList',
        'orderedList',
        'link',
        'unlink',
        'blockquote',
        'alignLeft',
        'alignCenter',
        'alignRight',
        'undo',
        'redo'
      ]
    },
    ref
  ) => {
    const editorRef = useRef<any>(null);

    const getToolbarItems = () => {
      const toolbarMap: Record<string, string> = {
        bold: 'bold',
        italic: 'italic',
        underline: 'underline',
        strike: 'strikethrough',
        clearFormatting: 'removeformat',
        h1: 'h1',
        h2: 'h2',
        h3: 'h3',
        h4: 'h4',
        bulletList: 'bullist',
        orderedList: 'numlist',
        link: 'link',
        unlink: 'unlink',
        blockquote: 'blockquote',
        hr: 'hr',
        undo: 'undo',
        redo: 'redo',
        alignLeft: 'alignleft',
        alignCenter: 'aligncenter',
        alignRight: 'alignright',
        alignJustify: 'alignjustify',
        superscript: 'superscript',
        subscript: 'subscript',
        highlight: 'highlight',
        color: 'forecolor'
      };

      let toolbar = controls
        .map((control) => toolbarMap[control])
        .filter(Boolean)
        .join(' ');

      // Add gallery and slider buttons if enabled
      if (withGallery) {
        toolbar += ' gallery';
      }
      if (withSlider) {
        toolbar += ' slider';
      }

      return toolbar;
    };

    const getPlugins = () => {
      const plugins = new Set([
        'advlist',
        'autolink',
        'lists',
        'link',
        'image',
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'code',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'wordcount'
      ]);

      controls.forEach((control) => {
        if (control === 'bulletList' || control === 'orderedList') {
          plugins.add('lists');
        } else if (control === 'link' || control === 'unlink') {
          plugins.add('link');
        } else if (control === 'hr') {
          plugins.add('hr');
        } else if (control === 'color') {
          plugins.add('textcolor');
        } else if (control === 'highlight') {
          plugins.add('textcolor');
        }
      });

      return Array.from(plugins).join(' ');
    };

    const handleEditorChange = (content: string) => {
      onChange?.(content);
    };

    const handleEditorInit = (evt: any, editor: any) => {
      editorRef.current = editor;

      if (value) {
        editor.setContent(value);
      }

      // Register gallery button if enabled
      if (withGallery) {
        editor.ui.registry.addButton('gallery', {
          text: 'Gallery',
          icon: 'gallery',
          onAction: () => openGalleryDialog(editor)
        });

        editor.ui.registry.addMenuItem('gallery', {
          text: 'Photo Gallery',
          icon: 'gallery',
          onAction: () => openGalleryDialog(editor)
        });
      }

      // Register slider button if enabled
      if (withSlider) {
        editor.ui.registry.addButton('slider', {
          text: 'Slider',
          icon: 'gallery',
          onAction: () => openSliderDialog(editor)
        });

        editor.ui.registry.addMenuItem('slider', {
          text: 'Image Slider',
          icon: 'gallery',
          onAction: () => openSliderDialog(editor)
        });
      }

      // Add custom menu items if gallery or slider is enabled
      if (withGallery || withSlider) {
        const submenuItems: {
          type: string;
          text: string;
          icon: string;
          onAction: (() => void) | (() => void);
        }[] = [];
        if (withGallery) {
          submenuItems.push({
            type: 'menuitem',
            text: 'Photo Gallery',
            icon: 'gallery',
            onAction: () => openGalleryDialog(editor)
          });
        }
        if (withSlider) {
          submenuItems.push({
            type: 'menuitem',
            text: 'Image Slider',
            icon: 'gallery',
            onAction: () => openSliderDialog(editor)
          });
        }

        editor.ui.registry.addNestedMenuItem('customimages', {
          text: 'Custom Images',
          icon: 'gallery',
          getSubmenuItems: () => submenuItems
        });
      }

      // Handle click events for interactive elements
      editor.on('click', function (e: any) {
        const target = e.target as HTMLElement;
        if (target.classList.contains('slider-image')) {
          const link = target.closest('a');
          if (link) {
            editor.windowManager.open({
              title: 'Edit Image Link',
              body: {
                type: 'panel',
                items: [
                  {
                    type: 'input',
                    name: 'url',
                    label: 'URL',
                    value: link.getAttribute('href') !== '#' ? link.getAttribute('href') : ''
                  }
                ]
              },
              buttons: [
                { type: 'cancel', text: 'Cancel' },
                { type: 'submit', text: 'Save', primary: true }
              ],
              onSubmit: function (api: any) {
                const data = api.getData();
                link.setAttribute('href', data.url || '#');
                api.close();
              }
            });
          }
        }
      });
    };

    const uploadMultipleImages = async (
      files: FileList,
      progressCallback: (fileIndex: number, totalFiles: number, progress: number) => void
    ) => {
      if (!uploadHandler) {
        throw new Error('Upload handler not provided');
      }

      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const blobInfo = {
          blob: () => file,
          filename: () => file.name
        };

        try {
          const url = await uploadHandler(blobInfo, (progress) => {
            progressCallback(i + 1, files.length, progress);
          });
          uploadedUrls.push(url);
        } catch (error) {
          console.error('Error uploading image:', error);
          throw error;
        }
      }

      return uploadedUrls;
    };

    const openGalleryDialog = (editor: any) => {
      editor.windowManager.open({
        title: 'Insert Photo Gallery',
        body: {
          type: 'panel',
          items: [
            {
              type: 'selectbox',
              name: 'columns',
              label: 'Columns per row',
              items: [
                { value: '2', text: '2 Columns' },
                { value: '3', text: '3 Columns' },
                { value: '4', text: '4 Columns' }
              ]
            },
            {
              type: 'htmlpanel',
              html: `
                <div style="margin: 10px 0;">
                  <input type="file" id="gallery-images" multiple accept="image/*" style="display: none;"/>
                  <button onclick="document.getElementById('gallery-images').click()" 
                          style="padding: 8px 16px; background: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer;">
                    Select Images
                  </button>
                  
                  <div id="gallery-images-container" style="margin-top: 15px;">
                    <!-- Images with URL inputs will be inserted here -->
                  </div>
                </div>
              `
            }
          ]
        },
        buttons: [
          { type: 'cancel', text: 'Cancel' },
          { type: 'submit', text: 'Insert', primary: true }
        ],
        onSubmit: async (api: any) => {
          const container = document.getElementById('gallery-images-container');
          const images = container?.querySelectorAll('.gallery-preview-item');
          const imageData: Array<{ src: string; link: string }> = [];

          images?.forEach((item) => {
            const img = item.querySelector('img');
            const urlInput = item.querySelector('input[type="text"]') as HTMLInputElement;

            if (img) {
              imageData.push({
                src: img.src,
                link: urlInput?.value.trim() || ''
              });
            }
          });

          insertGallery(editor, imageData, api.getData().columns);
          api.close();
        }
      });

      // Setup file input handler
      setTimeout(() => {
        const fileInput = document.getElementById('gallery-images') as HTMLInputElement;
        if (fileInput) {
          fileInput.onchange = (e) => {
            const files = (e.target as HTMLInputElement).files;
            const container = document.getElementById('gallery-images-container');
            if (container && files) {
              container.innerHTML = '';

              Array.from(files).forEach((file, index) => {
                const reader = new FileReader();
                const previewItem = document.createElement('div');
                previewItem.className = 'gallery-preview-item';
                previewItem.style.cssText =
                  'display: flex; align-items: center; gap: 10px; margin-bottom: 10px; padding: 10px; border: 1px solid #ddd; border-radius: 4px;';

                reader.onload = (e) => {
                  previewItem.innerHTML = `
                    <img src="${e.target?.result}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px;" />
                    <div style="flex-grow: 1;">
                      <div style="margin-bottom: 5px;">Image ${index + 1}</div>
                      <input type="text" placeholder="Enter URL (optional)" style="width: 100%; padding: 5px; border: 1px solid #ddd; border-radius: 4px" />
                    </div>
                  `;
                  container.appendChild(previewItem);
                };

                reader.readAsDataURL(file);
              });
            }
          };
        }
      }, 100);
    };

    const openSliderDialog = (editor: any) => {
      editor.windowManager.open({
        title: 'Insert Image Slider',
        body: {
          type: 'panel',
          items: [
            {
              type: 'htmlpanel',
              html: `
                <div style="margin: 10px 0;">
                  <input type="file" id="slider-images" multiple accept="image/*" style="display: none;"/>
                  <button onclick="document.getElementById('slider-images').click()" 
                          style="padding: 8px 16px; background: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer;">
                    Select Images
                  </button>
                  
                  <div id="slider-images-container" style="margin-top: 15px;">
                    <!-- Images with URL inputs will be inserted here -->
                  </div>
                </div>
              `
            }
          ]
        },
        buttons: [
          { type: 'cancel', text: 'Cancel' },
          { type: 'submit', text: 'Insert', primary: true }
        ],
        onSubmit: async (api: any) => {
          const container = document.getElementById('slider-images-container');
          const images = container?.querySelectorAll('.slider-preview-item');
          const imageData: Array<{ src: string; link: string }> = [];

          images?.forEach((item) => {
            const img = item.querySelector('img');
            const urlInput = item.querySelector('input[type="text"]') as HTMLInputElement;

            if (img) {
              imageData.push({
                src: img.src,
                link: urlInput?.value.trim() || ''
              });
            }
          });

          insertSlider(editor, imageData);
          api.close();
        }
      });

      // Setup file input handler
      setTimeout(() => {
        const fileInput = document.getElementById('slider-images') as HTMLInputElement;
        if (fileInput) {
          fileInput.onchange = (e) => {
            const files = (e.target as HTMLInputElement).files;
            const container = document.getElementById('slider-images-container');
            if (container && files) {
              container.innerHTML = '';

              Array.from(files).forEach((file, index) => {
                const reader = new FileReader();
                const previewItem = document.createElement('div');
                previewItem.className = 'slider-preview-item';
                previewItem.style.cssText =
                  'display: flex; align-items: center; gap: 10px; margin-bottom: 10px; padding: 10px; border: 1px solid #ddd; border-radius: 4px;';

                reader.onload = (e) => {
                  previewItem.innerHTML = `
                    <img src="${e.target?.result}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px;" />
                    <div style="flex-grow: 1;">
                      <div style="margin-bottom: 5px;">Image ${index + 1}</div>
                      <input type="text" placeholder="Enter URL (optional)" style="width: 100%; padding: 5px; border: 1px solid #ddd; border-radius: 4px" />
                    </div>
                  `;
                  container.appendChild(previewItem);
                };

                reader.readAsDataURL(file);
              });
            }
          };
        }
      }, 100);
    };

    const insertGallery = (
      editor: any,
      images: Array<{ src: string; link: string }>,
      columns: string
    ) => {
      const galleryHtml = `
        <div class="photo-gallery" data-columns="${columns}">
          <div class="gallery-grid cols-${columns}">
            ${images
              .map(
                (image) => `
              <div class="gallery-item">
                ${
                  image.link
                    ? `<a href="${image.link}" target="_blank">
                    <img class="gallery-image" src="${image.src}" />
                  </a>`
                    : `<img class="gallery-image" src="${image.src}" />`
                }
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      `;
      editor.insertContent(galleryHtml);
    };

    const insertSlider = (editor: any, images: Array<{ src: string; link: string }>) => {
      const sliderHtml = `
        <div class="image-slider" data-current-slide="0">
          <div class="slider-container">
            ${images
              .map(
                (image, index) => `
              <div class="slider-slide" style="display: ${index === 0 ? 'block' : 'none'}">
                ${
                  image.link
                    ? `<a href="${image.link}" target="_blank">
                    <img class="slider-image" src="${image.src}" />
                  </a>`
                    : `<img class="slider-image" src="${image.src}" />`
                }
              </div>
            `
              )
              .join('')}
          </div>
          <button class="slider-prev">&larr;</button>
          <button class="slider-next">&rarr;</button>
        </div>
      `;
      editor.insertContent(sliderHtml);
    };

    return (
      <Box>
        {label && (
          <Text size='sm' fw={500} mb={5}>
            {label}
            {required && (
              <Text component='span' c='red' ml={4}>
                *
              </Text>
            )}
          </Text>
        )}

        <Box
          ref={ref}
          style={{
            ...(error && {
              border: '1px solid var(--mantine-color-error)',
              borderRadius: '4px'
            })
          }}
        >
          <Editor
            tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/js/tinymce/tinymce.min.js'}
            ref={editorRef}
            value={value}
            onInit={handleEditorInit}
            onEditorChange={handleEditorChange}
            onBlur={onBlur}
            disabled={disabled}
            init={{
              height: height || minHeight,
              min_height: minHeight,
              automatic_uploads: true,
              images_upload_handler: uploadHandler,
              plugins: getPlugins(),
              toolbar: getToolbarItems(),
              toolbar_sticky: false,
              menubar:
                withGallery || withSlider ? 'file edit view insert format tools table help' : false,
              menu:
                withGallery || withSlider
                  ? {
                      insert: {
                        title: 'Insert',
                        items:
                          'image link media customimages template codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor tableofcontents | insertdatetime'
                      }
                    }
                  : {},
              content_style: `
                body { 
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; 
                  font-size: 14px; 
                }
                
                .photo-gallery { width: 100%; margin: 20px 0; }
                .gallery-grid { display: grid; gap: 10px; }
                .gallery-grid.cols-2 { grid-template-columns: repeat(2, 1fr); }
                .gallery-grid.cols-3 { grid-template-columns: repeat(3, 1fr); }
                .gallery-grid.cols-4 { grid-template-columns: repeat(4, 1fr); }
                .gallery-item { position: relative; padding-bottom: 100%; }
                .gallery-image { 
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                  cursor: pointer;
                  transition: transform 0.3s ease;
                }
                .gallery-image:hover { transform: scale(1.05); }
                
                .image-slider { 
                  position: relative; 
                  width: 100%; 
                  max-width: 800px; 
                  margin: 20px auto;
                  background: #f5f5f5;
                  padding: 10px;
                  border-radius: 8px;
                }
                .slider-container { 
                  width: 100%; 
                  position: relative;
                  aspect-ratio: 16/9;
                  overflow: hidden;
                }
                .slider-image { 
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  object-fit: contain;
                }
                .slider-prev, .slider-next {
                  cursor: pointer;
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  width: 40px;
                  height: 40px;
                  color: white;
                  font-size: 16px;
                  background: rgba(0,0,0,0.5);
                  border: none;
                  border-radius: 50%;
                  transition: background 0.3s ease;
                  z-index: 2;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                .slider-prev:hover, .slider-next:hover {
                  background: rgba(0,0,0,0.8);
                }
                .slider-prev { left: 10px; }
                .slider-next { right: 10px; }
              `,
              placeholder: placeholder,
              branding: false,
              statusbar: false,
              resize: false,
              textcolor_map: [
                '25262b',
                'Dark Gray',
                '868e96',
                'Gray',
                'fa5252',
                'Red',
                'e64980',
                'Pink',
                'be4bdb',
                'Purple',
                '7950f2',
                'Violet',
                '4c6ef5',
                'Blue',
                '228be6',
                'Light Blue',
                '15aabf',
                'Cyan',
                '12b886',
                'Teal',
                '40c057',
                'Green',
                '82c91e',
                'Light Green',
                'fab005',
                'Yellow',
                'fd7e14',
                'Orange'
              ]
            }}
          />
        </Box>

        {error && (
          <Text size='xs' c='red' mt={5}>
            {error}
          </Text>
        )}
      </Box>
    );
  }
);

RichTextEditorInput.displayName = 'RichTextEditorInput';

export default RichTextEditorInput;
